import axios from 'axios';
import {customAuthAxios} from "../../customAuthAxios";
import {metaApiUrl} from "@/apiUrls/metaApiUrls";

const instagramGraphApiUrl = 'https://graph.instagram.com/v22.0/';
// プロフィールメトリクス（フォロワー数など）を取得する関数
export const fetchProfileMetrics = async (businessAccountId: string, accessToken: string) => {
    try {
        const response = await axios.get(`${metaApiUrl}${businessAccountId}`, {
            params: {
                fields: 'username,id,followers_count,follows_count',
                access_token: accessToken,
            },
        });

        console.log("Fetched profile response:", response.data);

        // レスポンスの構造を確認
        const data = response.data;

        // より詳細なログ出力
        console.log("followers_count:", data.followers_count);
        console.log("follows_count:", data.follows_count);
        console.log("username:", data.username);
        console.log("id:", data.id);

        return {
            followerCount: data.followers_count || 0,
            followingCount: data.follows_count || 0,
            username: data.username || '',
            id: data.id || '',
        };
    } catch (error) {
        console.error("Error fetching profile metrics:", error);
        // エラーが発生した場合はデフォルト値を返す
        return {
            followerCount: 0,
            followingCount: 0,
            username: '',
            id: '',
        };
    }
};


// メディアデータ（投稿のキャプションやURLなど）を取得する関数
export const fetchMediaData = async (
    businessAccountId: string,
    accessToken: string,
    source: 'facebook' | 'instagram' = 'facebook' // デフォルトをfacebookに
) => {
    const allMedia: any[] = [];

    let nextPageUrl = '';

    if (source === 'instagram') {
        // Instagramログインの場合: App-Scoped User ID + IG Graph API
        nextPageUrl = `${instagramGraphApiUrl}me/media?fields=id,caption,media_type,media_url,permalink,timestamp&access_token=${accessToken}`;
    } else {
        // Facebookログイン（従来通り）
        nextPageUrl = `${metaApiUrl}${businessAccountId}/media?fields=id,caption,media_type,media_url,permalink,like_count,comments_count,timestamp&limit=100&access_token=${accessToken}`;
    }

    try {
        while (nextPageUrl) {
            let response: any = [];
            if (source === 'instagram') {
                 response = await axios.post(nextPageUrl);
            }else{
                 response = await axios.get(nextPageUrl);
            }
            console.log("Fetched media response:", response.data);

            const result = response.data;

            if (result.data && Array.isArray(result.data)) {

                const viewsList = await Promise.all(
                    result.data.map((media: any) =>
                        source === 'facebook'
                            ? fetchViewsFromInsights(media.id, accessToken)
                            : Promise.resolve(0) // Instagramログインではviewsは取得できない（エンドポイント未対応）
                    )
                );

                const merged = result.data.map((media: any, index: number) => ({
                    ...media,
                    views: viewsList[index],
                }));

                allMedia.push(...merged);
            }

            if (result.paging && result.paging.next) {
                nextPageUrl = result.paging.next;
            } else {
                nextPageUrl = '';
            }
        }

        return allMedia;
    } catch (error) {
        console.error("メディアデータ取得中エラー:", error);
        return allMedia;
    }
};

// viewsだけを取得する関数
const fetchViewsFromInsights = async (mediaId: string, accessToken: string): Promise<number> => {
    try {
        const response = await axios.get(`https://graph.facebook.com/v22.0/${mediaId}/insights`, {
            params: {
                metric: 'views',
                access_token: accessToken,
            },
        });
        // console.log("Fetched views response:", response.data);
        const insights = response.data.data.find((metric: any) => metric.name === 'views');
        return insights?.values?.[0]?.value || 0;
    } catch (error) {
        console.error(`インサイト取得失敗 (mediaId=${mediaId}):`, error);
        return 0;
    }
};

// メディアインサイト（インプレッションやリーチなど）を取得する関数
export const fetchMediaInsights = async (
    mediaId: string,
    accessToken: string,
    mediaType: string
) => {
    let metrics = '';

    if (mediaType === 'IMAGE' || mediaType === 'CAROUSEL_ALBUM') {
        metrics = 'reach,likes,comments,saved';
    } else if (mediaType === 'VIDEO' || mediaType === 'REEL') {
        metrics = 'views,reach,likes,comments,saved';
    } else if (mediaType === 'STORY') {
        metrics = 'impressions,reach,reply,saved,exits';
    } else {
        console.error("Unsupported media type:", mediaType);
        return [];
    }

    try {
        const response = await axios.get(metaApiUrl + `${mediaId}/insights`, {
            params: {
                metric: metrics,
                access_token: accessToken,
            },
        });

        // console.log("Fetched insights response:", response.data);

        const defaultInsights = {
            engagement: 0,
            reach: 0,
            likes: 0,
            comments: 0,
            saved: 0,
            views: 0,
        };

        response.data.data.forEach((insight: any) => {
            if (insight.name in defaultInsights) {
                defaultInsights[insight.name as keyof typeof defaultInsights] = insight.values[0].value;
            }
        });

        return Object.keys(defaultInsights).map((key) => ({
            title: key,
            value: defaultInsights[key as keyof typeof defaultInsights],
        }));
    } catch (error) {
        console.error("Error fetching insights for media:", error);
        return [];
    }
};

export const getDjangoToken = async () => {
    try {
        const response = await customAuthAxios.post("authen/jwt/create/", {
            username: process.env.REACT_APP_DJANGO_USERNAME,
            password: process.env.REACT_APP_DJANGO_PASSWORD,
        });
        console.log("Django JWT token obtained:", response.data.access);
        return response.data.access;
    } catch (error) {
        console.error("Error fetching Django JWT token:", error);
        return null;
    }
};

export const saveAuthData = async (
    restaurantId: string,
    accessToken: string,
    businessAccountId: string,
    djangoToken: string,
    pageId?: string
) => {
    try {
        const payload = {
            token: accessToken,
            instagram_id: businessAccountId,
            page_id: pageId || ''
        };

        const response = await customAuthAxios.post(
            `external_api/instagram-auth/${restaurantId}/`,
            payload,
            {
                headers: {
                    Authorization: `Bearer ${djangoToken}`,
                },
            }
        );
        console.log("Auth data saved successfully:", response.data);
    } catch (error) {
        console.error("Error saving auth data:", error);
    }
};

export const fetchAuthDataFromDjango = async (restaurantId: string) => {
    try {
        const djangoToken = await getDjangoToken();
        if (!djangoToken) {
            console.error("Failed to obtain Django token.");
            return null;
        }
        const response = await customAuthAxios.get(`external_api/instagram-auth/${restaurantId}/`, {
            headers: {
                Authorization: `Bearer ${djangoToken}`,
            },
        });

        console.log("Auth data from Django:", response.data);
        return response.data;
    } catch (error) {
        console.error("Error fetching auth data from Django:", error);
        return null;
    }
};
